import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  ReactiveFormsModule
} from '@angular/forms';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { User } from '../models/user.model';


@Component({
  selector: 'app-physician-nppa-user-child',
  templateUrl: './physician-nppa-user-child.component.html',
  standalone: true,
  styleUrls: ['./physician-nppa-user-child.component.scss'],
  imports: [ReactiveFormsModule, NgIf, NgClass, NgForOf] // Import ReactiveFormsModule here
})
export class PhysicianNppaUserChildComponent implements OnInit {
  @Input() parentForm!: FormGroup; // Receive the parent form group
  @Input() editProfile!: boolean;
  @Input() canEditProfileAttribute!: boolean;
  @Input() profileLoading!: boolean;
  @Input() user: User;

  public stateCodes: string[] = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
    'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
    'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  constructor(private fb: FormBuilder) {
  }

  ngOnInit(): void {
    if (!this.parentForm) {
      console.error('Parent form is not provided!');
    }

    // Add child-specific controls to the parent form group
    this.parentForm.addControl('clinicMedCenter', this.fb.control(''));
    this.parentForm.addControl('fax', this.fb.control('', [
      Validators.required,
      Validators.pattern(/^[2-9][0-9]{9}$/) // Must not start with "1", must be 10 digits, and only numbers, // Example: 5-digit ZIP code
    ]));
    this.parentForm.addControl('addressLine1', this.fb.control('', Validators.required));
    this.parentForm.addControl('pager', this.fb.control('', [
      Validators.pattern(/^[2-9][0-9]{9}$/) // Must not start with "1", must be 10 digits, and only numbers, // Example: 5-digit ZIP code
    ]));
    this.parentForm.addControl('addressLine2', this.fb.control(''));
    this.parentForm.addControl('directEmail', this.fb.control('', [
      Validators.email // Validates the email format
    ]));
    this.parentForm.addControl('city', this.fb.control('', Validators.required));
    this.parentForm.addControl('state', this.fb.control('', Validators.required));
    this.parentForm.addControl('zip', this.fb.control('', [
      Validators.required,
      Validators.pattern(/^\d{5}$/) // Must be exactly 5 digits
    ]));
    this.parentForm.addControl('upin', this.fb.control('', [
      Validators.pattern(/^[a-zA-Z0-9]*$/) // Alphanumeric characters only
    ]));
    this.parentForm.addControl('taxonomyCode', this.fb.control(''));
    this.parentForm.addControl('licenseNumber', this.fb.control(''));
    this.parentForm.addControl('npi', this.fb.control('', [Validators.required, npiValidator()]));
    this.parentForm.addControl('taxIdNumber', this.fb.control(''));
    this.parentForm.addControl('medicaidNumber', this.fb.control(''));
    this.parentForm.addControl('stateDeaNumbers', this.fb.array([this.createStateDeaGroup()])); // Add FormArray to parentForm
    this.parentForm.addControl('medicareNumber', this.fb.control(''));
    this.parentForm.addControl('nadeanNumber', this.fb.control(''));

    // Populate the stateDeaNumbers array if user.stateDeaNumbers has values
    this.setStateDeaNumbers();
    // Ensure at least one blank field is displayed
    this.ensureAtLeastOneStateDeaNumber();
  }

// Getter for the FormArray
  get stateDeaNumbers(): FormArray {
    return this.parentForm.get('stateDeaNumbers') as FormArray;
  }

// Helper method to create a FormGroup for stateDeaNumbers
  createStateDeaGroup(stateDeaNumber: any = {}): FormGroup {
    const deaControl = this.fb.control(stateDeaNumber.deaNumber ?? '', [deaValidator()]);

    return this.fb.group({
      stateForDeaNumber: [stateDeaNumber.stateForDeaNumber ?? ''],
      deaNumber: deaControl,
    }, { validators: this.conditionalRequiredValidator });
  }

  public ensureAtLeastOneStateDeaNumber(): void {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;

    // Add a blank FormGroup if the FormArray is empty
    if (stateDeaNumbers.length === 0) {
      stateDeaNumbers.push(this.createStateDeaGroup());
    }
  }

  conditionalRequiredValidator(group: AbstractControl): ValidationErrors | null {
    const state = group.get('stateForDeaNumber')?.value;
    const deaNumber = group.get('deaNumber')?.value;

    if ((state && !deaNumber) || (!state && deaNumber)) {
      return { conditionalRequired: true }; // Error if one field is filled but the other is empty
    }
    return null; // No error if both fields are empty or both are filled
  }

  public setStateDeaNumbers() {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;

    // Clear the existing FormArray
    stateDeaNumbers.clear();

    // Populate the FormArray with user.stateDeaNumbers
    if (this.user?.stateDeaNumbers?.length) {
      this.user.stateDeaNumbers.forEach((stateDeaNumber: any) => {
        stateDeaNumbers.push(this.createStateDeaGroup(stateDeaNumber));
      });
    }
  }

// Method to add a new row
  addStateDeaNumber() {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;

    // Check if the last entry in the array has both fields blank
    const lastEntry = stateDeaNumbers.at(stateDeaNumbers.length - 1);
    if (lastEntry) {
      const state = lastEntry.get('stateForDeaNumber')?.value;
      const deaNumber = lastEntry.get('deaNumber')?.value;

      if (!state && !deaNumber) {
        return; // Do not add a new entry if both fields are blank
      }
    }

    // Add a new entry if validation passes
    stateDeaNumbers.push(this.createStateDeaGroup());
  }

// Method to remove a row
  removeStateDeaNumber(index: number) {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;
    stateDeaNumbers.removeAt(index);

    // Trigger validation on all remaining DEA controls to update duplicate validation
    this.validateAllDeaNumbers();
  }

  // Methods to handle DEA validation triggering
  onDeaNumberInput(index: number): void {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;
    const deaControl = stateDeaNumbers.at(index).get('deaNumber');
    if (deaControl) {
      deaControl.markAsDirty();
      deaControl.updateValueAndValidity();

      // Trigger validation on all other DEA controls to check for duplicates
      this.validateAllDeaNumbers();
    }
  }

  onDeaNumberBlur(index: number): void {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;
    const deaControl = stateDeaNumbers.at(index).get('deaNumber');
    if (deaControl) {
      deaControl.markAsTouched();
      deaControl.updateValueAndValidity();

      // Trigger validation on all other DEA controls to check for duplicates
      this.validateAllDeaNumbers();
    }
  }

  /**
   * Validates all DEA number controls to ensure duplicate validation is updated
   * across all controls when one changes
   */
  public validateAllDeaNumbers(): void {
    const stateDeaNumbers = this.parentForm.get('stateDeaNumbers') as FormArray;
    stateDeaNumbers?.controls.forEach(group => {
      const deaControl = group.get('deaNumber');
      deaControl?.updateValueAndValidity({ emitEvent: false });
    });
  }
}

export function deaValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null; // Allow empty values
    }

    const deaNumber = value.toString().trim().toUpperCase();

    // Pattern validation: ^[ABFGMPR][A-Z][0-9]{7}$ (case insensitive)
    const deaPattern = /^[ABFGMPR][A-Z]\d{7}$/i;

    if (!deaPattern.test(deaNumber)) {
      return {
        invalidDea: 'DEA Number must be 9 characters and needs to follow the criteria (e.g., AB1234567)'
      };
    }

    // Checksum validation (matching backend logic exactly)
    if (!isValidDeaChecksum(deaNumber)) {
      return {
        invalidDea: 'DEA Number Last Character need to follow criteria'
      };
    }

    // Duplicate validation - check if this DEA number exists elsewhere in the form
    if (isDuplicateDeaNumber(control, deaNumber)) {
      return {
        invalidDea: 'DEA Number already exists. Each DEA Number must be unique.'
      };
    }

    return null;
  };
}

/**
 * Helper function to check for duplicate DEA numbers within the same form
 * @param control - The current form control being validated
 * @param deaNumber - The DEA number to check for duplicates (already normalized to uppercase)
 * @returns true if duplicate found, false otherwise
 */
function isDuplicateDeaNumber(control: AbstractControl, deaNumber: string): boolean {
  // Navigate up to find the FormArray containing all DEA numbers
  // Control hierarchy: deaNumber control -> FormGroup -> FormArray
  const formGroup = control.parent;
  if (!formGroup) return false;

  const formArray = formGroup.parent as FormArray;
  if (!formArray?.controls) return false;

  // Count occurrences of this DEA number in the FormArray
  let count = 0;
  for (const group of formArray.controls) {
    const deaControl = group.get('deaNumber');
    if (deaControl?.value) {
      const otherDeaNumber = deaControl.value.toString().trim().toUpperCase();
      if (otherDeaNumber === deaNumber) {
        count++;
      }
    }
  }

  // If count > 1, it means this DEA number appears multiple times
  return count > 1;
}

function isValidDeaChecksum(deaNumber: string): boolean {
  // Extract the numeric characters (positions 2-8, 0-indexed)
  const thirdChar = parseInt(deaNumber.charAt(2));
  const fourthChar = parseInt(deaNumber.charAt(3));
  const fifthChar = parseInt(deaNumber.charAt(4));
  const sixthChar = parseInt(deaNumber.charAt(5));
  const seventhChar = parseInt(deaNumber.charAt(6));
  const eighthChar = parseInt(deaNumber.charAt(7));
  const ninthChar = parseInt(deaNumber.charAt(8));

  // Calculate checksum exactly as backend does
  const firstSequence = thirdChar + fifthChar + seventhChar;
  const secondSequence = (fourthChar + sixthChar + eighthChar) * 2;
  const finalSequence = (firstSequence + secondSequence) % 10;

  // Validate checksum
  return finalSequence === ninthChar;
}

export function npiValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) return null;

    const npi = value.toString();

    if (!/^\d{10}$/.test(npi)) {
      return { invalidNpi: 'NPI must be exactly 10 digits' };
    }

    const isValid = isValidNpi(npi);
    return isValid ? null : { invalidNpi: 'NPI failed Luhn check' };
  };
}

function isValidNpi(npi: string): boolean {
  const prefix = '80840';
  const fullNumber = prefix + npi;

  let sum = 0;
  const digits = fullNumber.split('').map(Number).reverse();

  for (let i = 0; i < digits.length; i++) {
    let digit = digits[i];
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  return sum % 10 === 0;
}

/*
export function npiLuhnValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const npi = control.value?.toString().trim(); // Ensure input is a string and trim whitespace
    console.log('NPI Input:', npi); // Log the input value

    if (!npi) {
      return null; // No error if the field is empty
    }

    // NPI must be 10 digits
    if (!/^\d{10}$/.test(npi)) {
      console.log('Invalid Format: NPI must be 10 digits');
      return { invalidNpi: true };
    }

    // LUHN algorithm for NPI
    const isValidNpi = (num: string): boolean => {
      let sum = 0;
      let shouldDouble = false;

      // Process digits from right to left
      for (let i = num.length - 1; i >= 0; i--) {
        let digit = parseInt(num.charAt(i), 10);
        console.log('Digit:', digit);
        if (shouldDouble) {
          digit *= 2;
          if (digit > 9) {
            digit -= 9;
          }
        }

        sum += digit;
        shouldDouble = !shouldDouble;
      }
      console.log('LUHN Sum:', sum);
      return sum % 10 === 0;
    };

    const result = isValidNpi(npi);
    console.log('Validation Result:', result); // Log the validation result
    return result ? null : { invalidNpi: true };
  };
}
*/
